import { PaymentElement } from "@stripe/react-stripe-js";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "@/atoms/alert";
import { Button } from "@/atoms/button";
import { CardFieldsBand } from "@/components/card-fields-skeleton";
import { Checkbox } from "@/atoms/checkbox";
import { Field } from "@/atoms/field";
import { FactList, FactRow } from "@/components/fact-row";
import { CheckoutWallet } from "@/components/checkout-wallet";
import { StripeLine } from "@/components/stripe-line";
import { useOnline } from "@/components/use-online";
import { useInvoicePayment } from "./use-invoice-payment";
import { formatRunningMoney, fromCents } from "@/utils/money";
import type { InvoiceOutcome } from "./use-invoice-payment";
import type { PublicInvoiceDetails } from "@/data/invoice";

/**
 * The way to pay an invoice (checkout 1.4 regions `platformPay`, `form`, `consent`, `action`).
 *
 * The wallet, the card, the consent and one orange button. No coupon here (an invoice's amount
 * is already decided) and no totals block either: the amounts are the group above and saying
 * them twice would be the price said twice, which the template forbids.
 *
 * The email is the fix at the heart of this page. When the customer record carries one it is a
 * fact row with a tertiary Change beside it, so a person sees where the receipt goes without a
 * field to fill in; when it carries none the field is there and required, which is the thing the
 * old page never did.
 */

const GYMKEE_TERMS = "https://gymkee.com/terms";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface InvoiceSheetProps {
  invoice: PublicInvoiceDetails;
  coachName: string;
  onDone: (outcome: InvoiceOutcome) => void;
}

export function InvoiceSheet({
  invoice,
  coachName,
  onDone,
}: InvoiceSheetProps) {
  const { t } = useTranslation();
  const online = useOnline();
  const knownEmail = invoice.customer.email?.trim() ?? "";

  const [email, setEmail] = useState(knownEmail);
  const [editingEmail, setEditingEmail] = useState(!knownEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const [consent, setConsent] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardMounted, setCardMounted] = useState(false);
  const [walletAvailable, setWalletAvailable] = useState<boolean | null>(null);

  const { pay, paying, error, ready } = useInvoicePayment({
    invoiceId: invoice.invoice.id,
    onDone,
  });

  const emailValid = EMAIL_SHAPE.test(email.trim());
  const canPay = emailValid && consent && cardComplete && ready && !paying;
  const emailError =
    emailTouched && !emailValid ? t("gymkeePay.checkout.emailInvalid") : null;

  const submit = () => {
    if (!canPay) return;
    void pay(email.trim());
  };

  return (
    <form
      className="ds-pay-form gap-gk-6 flex flex-col"
      aria-busy={paying || undefined}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset
        disabled={paying}
        className="gap-gk-6 flex min-w-0 flex-col border-0 p-0"
      >
        {/* The wallet, and the band it stands in: the 48 skeleton while Stripe decides whether
            this device has a wallet at all, and nothing at all when it does not. */}
        <CheckoutWallet
          isSubscription={!!invoice.subscription}
          collectPhoneNumber={false}
          collectBillingAddress={false}
          available={walletAvailable}
          onAvailable={setWalletAvailable}
          onConfirm={(billing, failed) => {
            void pay(billing.email?.trim() || email.trim()).then((paid) => {
              if (!paid) failed();
            });
          }}
        />

        <div className="gap-gk-4 flex flex-col">
          {editingEmail ? (
            <Field
              label={t("gymkeePay.checkout.emailLabel")}
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setEmailTouched(true)}
              error={emailError}
              helper={
                knownEmail ? undefined : t("gymkeePay.invoice.emailNeeded")
              }
            />
          ) : (
            <FactList>
              <FactRow
                label={t("gymkeePay.invoice.receiptTo")}
                value={email}
                action={
                  <Button
                    variant="tertiary"
                    className="ds-pay-hand"
                    onClick={() => setEditingEmail(true)}
                  >
                    {t("gymkeePay.invoice.changeEmail")}
                  </Button>
                }
              />
            </FactList>
          )}

          {/* The card fields, and the band they will fill. Stripe paints nothing while the element
              mounts, so the sheet had a blank 240 gap here; the skeleton stands at the geometry
              the element resolves to and is lifted the moment Stripe says ready. */}
          <CardFieldsBand ready={cardMounted}>
            <PaymentElement
              options={{
                layout: { type: "tabs" },
                wallets: {
                  applePay: "never",
                  googlePay: "never",
                  link: "never",
                },
                fields: {
                  billingDetails: {
                    email: "never",
                    name: "auto",
                    address: "auto",
                  },
                },
                terms: { card: "never" },
              }}
              onChange={(event) => setCardComplete(event.complete)}
              onReady={() => setCardMounted(true)}
            />
          </CardFieldsBand>
        </div>

        <Checkbox
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          label={
            <Trans
              i18nKey={
                invoice.subscription
                  ? "gymkeePay.checkout.consentSubscription"
                  : "gymkeePay.checkout.consentOneOff"
              }
              values={{ coach: coachName }}
              components={[
                <span key="0" />,
                <a
                  key="1"
                  href={GYMKEE_TERMS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  style={{ color: "var(--gk-text-orange-ink)" }}
                />,
              ]}
            />
          }
        />

        <div className="gap-gk-3 flex flex-col">
          {error && <Alert tone="danger">{error}</Alert>}
          {online && (
            <>
              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                loading={paying}
                disabled={!canPay}
              >
                {t("gymkeePay.checkout.payAmount", {
                  amount: formatRunningMoney(
                    fromCents(invoice.invoice.amountDue),
                    invoice.invoice.currency,
                  ),
                })}
              </Button>
              {!canPay && !paying && (
                <p className="text-center text-[13px] text-[var(--gk-text-tertiary)]">
                  {t("gymkeePay.checkout.payHint")}
                </p>
              )}
            </>
          )}
          <StripeLine />
        </div>
      </fieldset>
    </form>
  );
}
